import { isAddress } from "viem";
import { z } from "zod";
import {
  ExecutionCorrelationIdSchema,
  ExecutionPortfolioIdSchema,
  ExecutionRequestKeySchema,
} from "./requests.js";
import { SellBalanceInputSchema } from "./sell-balance.js";

const address = z
  .string()
  .refine(
    (value) => isAddress(value) && !/^0x0{40}$/iu.test(value),
    "A nonzero EVM address is required",
  )
  .transform((value) => value.toLowerCase());
const isRaw = (value: string) =>
  value.length <= 78 && /^(?:0|[1-9]\d*)$/u.test(value);
const raw = z
  .string()
  .refine(
    (value) => isRaw(value) && BigInt(value) < 1n << 256n,
    "Amount must fit uint256",
  );
const positiveRaw = raw.refine(
  (value) => isRaw(value) && BigInt(value) > 0n,
  "Amount must be positive",
);
const asset = z
  .object({
    accountRef: z.string(),
    assetId: z.string(),
    amountRaw: positiveRaw,
  })
  .strict();
const shape = {
  portfolioId: ExecutionPortfolioIdSchema,
  expectedOwnerAddress: address,
  recipient: address,
  assets: z.array(asset).min(1).max(50),
};
const refineAssets = (
  input: { portfolioId: string; assets: readonly z.infer<typeof asset>[] },
  ctx: z.RefinementCtx,
) => {
  let previous: string | undefined;
  for (const [index, selection] of input.assets.entries()) {
    if (
      !SellBalanceInputSchema.safeParse({
        portfolioId: input.portfolioId,
        accountRef: selection.accountRef,
        assetId: selection.assetId,
      }).success ||
      selection.accountRef !== input.assets[0]?.accountRef ||
      (previous !== undefined && previous >= selection.assetId)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["assets", index],
        message:
          "Assets must be unique and sorted, on one canonical EVM account and chain",
      });
    }
    previous = selection.assetId;
  }
};
export const executionExternalWithdrawalPreviewInputSchema = z
  .object(shape)
  .strict()
  .superRefine(refineAssets);
export const executionExternalWithdrawalCreateSchema = z
  .object({
    ...shape,
    requestKey: ExecutionRequestKeySchema,
    correlationId: ExecutionCorrelationIdSchema.optional(),
    previewId: z.string().regex(/^[0-9a-f]{64}$/u),
  })
  .strict()
  .superRefine(refineAssets);
export const executionExternalWithdrawalPreviewSchema = z
  .object({
    ...shape,
    previewId: z.string().regex(/^[0-9a-f]{64}$/u),
    assets: z
      .array(
        asset
          .extend({
            recipientAmountRaw: raw,
            decimals: z.number().int().min(0).max(255),
          })
          .strict(),
      )
      .min(1)
      .max(50),
    fee: z
      .object({
        assetId: z.string(),
        amountRaw: positiveRaw,
        recipient: address,
        usd: z
          .string()
          .regex(/^(?:0|[1-9]\d*)(?:\.\d+)?$/u)
          .refine(
            (value) => Number.isFinite(Number(value)) && Number(value) > 0,
          ),
      })
      .strict(),
  })
  .strict()
  .superRefine((input, ctx) => {
    refineAssets(input, ctx);
    if (
      !isRaw(input.fee.amountRaw) ||
      input.assets.some(
        (selection) =>
          !isRaw(selection.amountRaw) || !isRaw(selection.recipientAmountRaw),
      )
    )
      return;
    if (
      !input.assets.some(
        (selection) => selection.assetId === input.fee.assetId,
      ) ||
      !input.assets.some(
        (selection) => BigInt(selection.recipientAmountRaw) > 0n,
      )
    ) {
      ctx.addIssue({
        code: "custom",
        message:
          "Fee must belong to the selected assets and leave a recipient transfer",
      });
    }
    for (const [index, selection] of input.assets.entries()) {
      if (
        BigInt(selection.amountRaw) !==
        BigInt(selection.recipientAmountRaw) +
          (selection.assetId === input.fee.assetId
            ? BigInt(input.fee.amountRaw)
            : 0n)
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["assets", index],
          message: "Gross amount must equal recipient amount plus fee",
        });
      }
    }
  });
export type ExecutionExternalWithdrawalPreviewInput = z.infer<
  typeof executionExternalWithdrawalPreviewInputSchema
>;
export type ExecutionExternalWithdrawalCreateInput = z.infer<
  typeof executionExternalWithdrawalCreateSchema
>;
export type ExecutionExternalWithdrawalPreview = z.infer<
  typeof executionExternalWithdrawalPreviewSchema
>;
